import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Printer, RotateCcw, Save } from 'lucide-react'
import { useReducer, useState, type ReactNode } from 'react'

import type { ReceiptDocumentKey } from '@aeris/contracts'
import {
  RECEIPT_NARROW_COLUMNS,
  renderReceiptDocumentHtml,
  type PaperWidth,
} from '@aeris/printing'
import {
  Button,
  Field,
  InlineAlert,
  Input,
  QueryState,
  Select,
  Switch,
  SwitchField,
  Textarea,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import { InfoOrderEditor } from '../components/info-order-editor.tsx'
import {
  fetchReceiptTemplate,
  saveReceiptTemplate,
} from '../lib/api/receipt-template.ts'
import { ApiError } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { printHtmlDocument } from '../lib/label-print.ts'
import { sampleDocumentFor } from '../lib/receipt-sample.ts'
import {
  DOCUMENT_KEYS,
  DOCUMENT_LABELS,
  draftReducer,
  emptyDraft,
  isDirty,
  linesFromText,
  overflowsPaper,
  PAPER_LABELS,
  textFromLines,
  type ReceiptDraft,
} from '../lib/receipt-template-draft.ts'
import { useCompanyHeader } from '../lib/use-receipt-template.ts'

/**
 * O modelo da nota térmica — F149.
 *
 * "Na parte de nota da impressora térmica tem que ter algo pra editar / preview,
 * igual tem das etiquetas." A etiqueta já tinha; a nota tinha o cabeçalho e o
 * rodapé escritos no código, e mudar "Obrigado pela preferência!" era um deploy.
 *
 * ## Por que formulário, e não a mesa de desenho da etiqueta
 *
 * A etiqueta é um retângulo fixo: cada campo tem o seu milímetro. A nota é uma
 * tira que cresce conforme os itens — arrastar um campo para "30 mm, 54 mm" não
 * quer dizer nada quando a lista de itens empurra tudo para baixo. O que a loja
 * escolhe aqui é TEXTO e o que aparece; a ordem dos blocos continua no código,
 * onde ela é revisada (ADR-0024).
 *
 * ## A prévia é o renderizador que imprime
 *
 * `renderReceiptDocumentHtml` — o mesmo HTML que vai para a impressora, com
 * negrito, TOTAL grande e o código de barras de verdade (F140). Uma prévia
 * desenhada à parte é uma segunda implementação que concorda com a primeira até
 * parar de concordar, e a discordância aparece na mão do cliente.
 */

const PX_PER_MM = 96 / 25.4

const PAPER_MM: Record<PaperWidth, number> = { 32: 58, 48: 80 }

/** O aviso do cupom é fixo; os outros papéis a loja escreve. */
const NOTICE_LOCKED: ReceiptDocumentKey = 'receipt'

/**
 * O editor, montado a partir de uma versão do modelo.
 *
 * O estado nasce das props e a página o remonta (`key`) quando chega uma versão
 * nova. É o que evita semear por efeito — que redesenha em cascata e, pior,
 * desfaria o que a pessoa acabou de digitar num refetch em segundo plano.
 */
const Editor = ({
  initial,
  version,
  canWrite,
  branchName,
}: {
  initial: ReceiptDraft
  version: number
  canWrite: boolean
  branchName: string
}): ReactNode => {
  const queryClient = useQueryClient()
  const company = useCompanyHeader()

  const [draft, dispatch] = useReducer(draftReducer, initial)
  const [baseline, setBaseline] = useState<ReceiptDraft>(initial)
  const [savedVersion, setSavedVersion] = useState(version)
  const [kind, setKind] = useState<ReceiptDocumentKey>('receipt')
  const [paper, setPaper] = useState<PaperWidth | null>(null)
  const [error, setError] = useState<string | null>(null)

  /*
   * O texto dos campos de várias linhas fica como a pessoa digitou.
   *
   * Se o campo mostrasse o modelo já normalizado, cada espaço digitado sumiria
   * na hora — "Volte sempre" viraria "Voltesempre" enquanto ela escreve. O
   * modelo recebe a versão aparada; a tela mostra a original.
   */
  const [headerText, setHeaderText] = useState(() =>
    textFromLines(initial.header.extraLines)
  )
  const [footerText, setFooterText] = useState(() =>
    textFromLines(initial.documents.receipt.footerLines)
  )

  const shownPaper = paper ?? draft.paper
  const document = sampleDocumentFor(kind, draft, company, branchName)
  const html = renderReceiptDocumentHtml(document, shownPaper)
  const dirty = isDirty(draft, baseline)
  const current = draft.documents[kind]

  const save = useMutation({
    mutationFn: async () =>
      await saveReceiptTemplate({ version: savedVersion, template: draft }),
    onSuccess: (response) => {
      setBaseline(response.template)
      setSavedVersion(response.version)
      dispatch({ type: 'reset', template: response.template })
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['receipt-template'] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar o modelo.'
      )
    },
  })

  const overflow = [...draft.header.extraLines, ...current.footerLines].some(
    (line) => overflowsPaper(line, shownPaper)
  )

  return (
    <div className="flex flex-col gap-4">
      <TabHeader
        title="Modelo da nota"
        description="O que sai impresso na bobina: cabeçalho da loja, o que aparece em cada item e o recado no rodapé de cada papel."
        actions={
          <>
            <Button
              type="button"
              variant="secondary"
              leadingIcon={<RotateCcw aria-hidden="true" className="h-4 w-4" />}
              disabled={!canWrite}
              onClick={() => {
                dispatch({ type: 'resetAll' })
                setHeaderText(textFromLines(emptyDraft().header.extraLines))
                setFooterText(
                  textFromLines(emptyDraft().documents[kind].footerLines)
                )
              }}
            >
              Voltar ao padrão
            </Button>
            <Button
              type="button"
              variant="primary"
              leadingIcon={<Save aria-hidden="true" className="h-4 w-4" />}
              loading={save.isPending}
              disabled={!canWrite || !dirty}
              onClick={() => {
                save.mutate()
              }}
            >
              Salvar
            </Button>
          </>
        }
      />

      {canWrite ? null : (
        <InlineAlert tone="info">
          Você pode ver o modelo, mas quem muda o que sai impresso é o dono, o
          admin ou o gerente.
        </InlineAlert>
      )}

      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto]">
        <div className="flex flex-col gap-5">
          <section className="flex flex-col gap-3">
            <h3 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
              Cabeçalho da loja
            </h3>
            <SwitchField
              label="CNPJ"
              description="Sai abaixo do nome da loja, quando o cadastro tem."
              checked={draft.header.showDocument}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({ type: 'header', patch: { showDocument: checked } })
              }}
            />
            <SwitchField
              label="Razão social"
              description="Além do nome fantasia, como no papel antigo."
              checked={draft.header.showLegalName}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({ type: 'header', patch: { showLegalName: checked } })
              }}
            />
            <SwitchField
              label="Inscrição estadual"
              checked={draft.header.showStateRegistration}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({
                  type: 'header',
                  patch: { showStateRegistration: checked },
                })
              }}
            />
            <SwitchField
              label="Endereço"
              checked={draft.header.showAddress}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({ type: 'header', patch: { showAddress: checked } })
              }}
            />
            <SwitchField
              label="CEP da loja"
              checked={draft.header.showPostalCode}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({ type: 'header', patch: { showPostalCode: checked } })
              }}
            />
            <SwitchField
              label="Telefone da loja"
              description="O do cadastro da empresa."
              checked={draft.header.showPhone}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({ type: 'header', patch: { showPhone: checked } })
              }}
            />
            <SwitchField
              label="Nome da filial"
              checked={draft.header.showBranch}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({ type: 'header', patch: { showBranch: checked } })
              }}
            />
            <Field
              label="Linhas extras"
              hint="Telefone, WhatsApp, horário. Uma por linha, até três."
            >
              {(field) => (
                <Textarea
                  {...field}
                  rows={3}
                  disabled={!canWrite}
                  value={headerText}
                  onChange={(event) => {
                    setHeaderText(event.target.value)
                    dispatch({
                      type: 'header',
                      patch: { extraLines: linesFromText(event.target.value) },
                    })
                  }}
                />
              )}
            </Field>
          </section>

          <section className="flex flex-col gap-3">
            <h3 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
              Itens e bobina
            </h3>
            <SwitchField
              label="A conta embaixo do item"
              description="“2 UN x 25,00”, embaixo do nome do produto."
              checked={draft.lines.showUnitMath}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({ type: 'lines', patch: { showUnitMath: checked } })
              }}
            />
            <SwitchField
              label="Código do produto"
              description="“008520 - CABO 2M”, como no papel antigo."
              checked={draft.lines.showCode}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({ type: 'lines', patch: { showCode: checked } })
              }}
            />
            <SwitchField
              label="Localização da peça"
              description="“Local: GAVETA 2”, embaixo do nome — para quem separa. Sai o local desta loja, do cadastro do produto."
              checked={draft.lines.showLocation}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({ type: 'lines', patch: { showLocation: checked } })
              }}
            />
            <SwitchField
              label="Desconto de cada item"
              description="Embaixo da conta do item, quando houve."
              checked={draft.lines.showLineDiscount}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({
                  type: 'lines',
                  patch: { showLineDiscount: checked },
                })
              }}
            />
            <SwitchField
              label="Marcar preço alterado"
              checked={draft.lines.showOverriddenMark}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({
                  type: 'lines',
                  patch: { showOverriddenMark: checked },
                })
              }}
            />
            <SwitchField
              label="Operador no cupom"
              checked={draft.receipt.showOperator}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({ type: 'receipt', patch: { showOperator: checked } })
              }}
            />
            <SwitchField
              label="Caixa no cupom"
              checked={draft.receipt.showTerminal}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({ type: 'receipt', patch: { showTerminal: checked } })
              }}
            />
            {/*
              Os dados do cliente no papel — F160.

              O gerente: "falta aparecer os dados do cliente"; o dono:
              endereço e WhatsApp "pro motoboy saber". Cada dado tem a sua
              chave, e o Consumidor Final nunca imprime nenhum.
            */}
            <SwitchField
              label="Endereço do cliente"
              description="Para o motoboy saber aonde ir."
              checked={draft.customer.showAddress}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({ type: 'customer', patch: { showAddress: checked } })
              }}
            />
            <SwitchField
              label="WhatsApp/telefone do cliente"
              checked={draft.customer.showPhone}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({ type: 'customer', patch: { showPhone: checked } })
              }}
            />
            <SwitchField
              label="CPF/CNPJ do cliente"
              description="Desligado por padrão: o papel vai na mão do motoboy."
              checked={draft.customer.showDocument}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({
                  type: 'customer',
                  patch: { showDocument: checked },
                })
              }}
            />
            <SwitchField
              label="Código do cliente"
              description="O código do sistema antigo, antes do nome."
              checked={draft.customer.showCode}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({ type: 'customer', patch: { showCode: checked } })
              }}
            />
            <SwitchField
              label="Endereço campo a campo"
              description="Rua, número, bairro, complemento, CEP e cidade, cada um na sua linha."
              checked={draft.customer.addressStyle === 'fields'}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({
                  type: 'customer',
                  patch: { addressStyle: checked ? 'fields' : 'line' },
                })
              }}
            />
            <SwitchField
              label="E-mail do cliente"
              checked={draft.customer.showEmail}
              disabled={!canWrite}
              onCheckedChange={(checked) => {
                dispatch({ type: 'customer', patch: { showEmail: checked } })
              }}
            />
            <InfoOrderEditor
              order={draft.infoOrder}
              disabled={!canWrite}
              onMove={(line, by) => {
                dispatch({ type: 'move', line, by })
              }}
            />
            <Field label="Bobina da loja" className="w-56">
              {(field) => (
                <Select
                  {...field}
                  disabled={!canWrite}
                  value={String(draft.paper)}
                  onChange={(event) => {
                    const next = event.target.value === '48' ? 48 : 32
                    dispatch({ type: 'paper', paper: next })
                    setPaper(next)
                  }}
                >
                  <option value="32">{PAPER_LABELS[32]}</option>
                  <option value="48">{PAPER_LABELS[48]}</option>
                </Select>
              )}
            </Field>
          </section>

          {/*
            A letra do papel — F161.

            O dono: "trocar a fonte e aumentar a fonte". O tamanho estica a
            letra para cima, e não para os lados: a bobina tem as colunas que
            tem, e uma letra mais larga cortaria o valor à direita.
          */}
          <section className="flex flex-col gap-3">
            <h3 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
              Letra do papel
            </h3>
            <div className="flex flex-wrap gap-3">
              <Field label="Fonte" className="w-56">
                {(field) => (
                  <Select
                    {...field}
                    disabled={!canWrite}
                    value={draft.font.family}
                    onChange={(event) => {
                      dispatch({
                        type: 'font',
                        patch: {
                          family: event.target
                            .value as ReceiptDraft['font']['family'],
                        },
                      })
                    }}
                  >
                    <option value="consolas">Consolas (padrão)</option>
                    <option value="courier">Courier New</option>
                    <option value="arial">Arial</option>
                  </Select>
                )}
              </Field>
              <Field label="Tamanho" className="w-56">
                {(field) => (
                  <Select
                    {...field}
                    disabled={!canWrite}
                    value={draft.font.size}
                    onChange={(event) => {
                      dispatch({
                        type: 'font',
                        patch: {
                          size: event.target
                            .value as ReceiptDraft['font']['size'],
                        },
                      })
                    }}
                  >
                    <option value="normal">Normal</option>
                    <option value="large">Grande</option>
                    <option value="extra">Maior</option>
                  </Select>
                )}
              </Field>
            </div>
            {/*
             * Sem "Negrito" — F211: a nota sai sempre em negrito, com o traço
             * engrossado. A letra fina saía cinza na térmica.
             */}
            <Field label="Validade do orçamento" className="w-72">
              {(field) => (
                <Select
                  {...field}
                  disabled={!canWrite}
                  value={draft.quoteValidity}
                  onChange={(event) => {
                    dispatch({
                      type: 'quoteValidity',
                      value: event.target.value === 'days' ? 'days' : 'date',
                    })
                  }}
                >
                  <option value="date">Válido até 20/09/2026</option>
                  <option value="days">Validade: 1 dia</option>
                </Select>
              )}
            </Field>
          </section>

          <section className="flex flex-col gap-3">
            <h3 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
              Textos de cada papel
            </h3>
            <Field label="Papel" className="w-72">
              {(field) => (
                <Select
                  {...field}
                  value={kind}
                  onChange={(event) => {
                    const next = event.target.value as ReceiptDocumentKey
                    setKind(next)
                    setFooterText(
                      textFromLines(draft.documents[next].footerLines)
                    )
                  }}
                >
                  {DOCUMENT_KEYS.map((key) => (
                    <option key={key} value={key}>
                      {DOCUMENT_LABELS[key]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label="Aviso do topo"
              hint={
                kind === NOTICE_LOCKED
                  ? 'Fixo no cupom: é o que diz ao cliente que este papel não é cupom fiscal.'
                  : 'Sai logo abaixo do título. Vazio não imprime linha.'
              }
            >
              {(field) => (
                <Input
                  {...field}
                  disabled={!canWrite || kind === NOTICE_LOCKED}
                  value={current.notice ?? ''}
                  onChange={(event) => {
                    dispatch({
                      type: 'document',
                      key: kind,
                      patch: {
                        notice:
                          event.target.value.trim() === ''
                            ? null
                            : event.target.value,
                      },
                    })
                  }}
                />
              )}
            </Field>

            <Field
              label="Rodapé"
              hint="O recado do fim do papel. Uma por linha, até três."
            >
              {(field) => (
                <Textarea
                  {...field}
                  rows={3}
                  disabled={!canWrite}
                  value={footerText}
                  onChange={(event) => {
                    setFooterText(event.target.value)
                    dispatch({
                      type: 'document',
                      key: kind,
                      patch: { footerLines: linesFromText(event.target.value) },
                    })
                  }}
                />
              )}
            </Field>

            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-(length:--text-sm)">
                <Switch
                  checked={current.showBarcode}
                  disabled={!canWrite}
                  onCheckedChange={(checked) => {
                    dispatch({
                      type: 'document',
                      key: kind,
                      patch: { showBarcode: checked },
                    })
                  }}
                />
                Código de barras
              </label>
              <label className="flex items-center gap-2 text-(length:--text-sm)">
                <Switch
                  checked={current.showDocumentId}
                  disabled={!canWrite}
                  onCheckedChange={(checked) => {
                    dispatch({
                      type: 'document',
                      key: kind,
                      patch: { showDocumentId: checked },
                    })
                  }}
                />
                Id do documento
              </label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!canWrite}
                onClick={() => {
                  dispatch({ type: 'resetDocument', key: kind })
                  setFooterText(
                    textFromLines(emptyDraft().documents[kind].footerLines)
                  )
                }}
              >
                Voltar ao texto padrão deste papel
              </Button>
            </div>
          </section>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Select
              aria-label="Papel da prévia"
              className="w-40"
              value={String(shownPaper)}
              onChange={(event) => {
                setPaper(event.target.value === '48' ? 48 : 32)
              }}
            >
              <option value="32">{PAPER_LABELS[32]}</option>
              <option value="48">{PAPER_LABELS[48]}</option>
            </Select>
            <Button
              type="button"
              variant="secondary"
              leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                printHtmlDocument(html).catch(() => {
                  setError('Não foi possível abrir a impressão. Tente de novo.')
                })
              }}
            >
              Imprimir teste
            </Button>
          </div>

          {overflow ? (
            <InlineAlert tone="warning">
              {`Tem linha maior que ${String(shownPaper === 32 ? RECEIPT_NARROW_COLUMNS : 48)} caracteres: ela vai quebrar em duas na bobina.`}
            </InlineAlert>
          ) : null}

          <iframe
            title={`Prévia da nota — ${DOCUMENT_LABELS[kind]}`}
            srcDoc={html}
            className="rounded-(--radius-md) border border-(--border-default) bg-white"
            style={{
              width: `${String(Math.ceil(PAPER_MM[shownPaper] * PX_PER_MM))}px`,
              height: '560px',
            }}
          />
          <p className="text-(length:--text-xs) text-(--text-muted)">
            Exemplo com uma venda inventada, pelo mesmo desenho que vai para a
            impressora.
          </p>
        </div>
      </div>
    </div>
  )
}

export const ModeloDeNotaPage = (): ReactNode => {
  const { allows, session } = useAuth()

  const current = useQuery({
    queryKey: ['receipt-template'],
    queryFn: async ({ signal }) => await fetchReceiptTemplate(signal),
  })

  return (
    <QueryState
      what="o modelo da nota"
      isPending={current.isPending}
      isError={current.isError}
      isEmpty={false}
      error={current.error}
      empty={null}
      onRetry={() => {
        void current.refetch()
      }}
    >
      {current.data === undefined ? null : (
        <Editor
          key={current.data.version}
          initial={current.data.template}
          version={current.data.version}
          canWrite={allows('organization.write')}
          branchName={session?.context?.branchName ?? 'Matriz'}
        />
      )}
    </QueryState>
  )
}
